import type { PlaybookRoutingGroupRule } from "@repo/app-types/entities/company";
import type { GroupId } from "@repo/app-types/entities/group";
import {
  asPlaybookId,
  type PlaybookId,
} from "@repo/app-types/entities/playbook";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { IconButton } from "@repo/ui/Buttons/iconButton";
import { useConfirmationModal } from "@repo/ui/hooks/useConfirmationModal";
import {
  MultiSelect,
  type MultiSelectOption,
} from "@repo/ui/Select/multiSelect";
import {
  SimpleSelect,
  type SimpleSelectOption,
} from "@repo/ui/Select/simpleSelect";
import { IconPencil, IconTrash } from "@tabler/icons-react";
import { type FC, useMemo, useState } from "react";

/**
 * A playbook option for routing rules. `createdAt` is used to pick the default
 * candidate (the oldest playbook) when a rule routes among multiple playbooks.
 */
export interface RoutingPlaybookOption {
  value: PlaybookId;
  label: string;
  createdAt: string;
}

interface RoutingRuleCardProps {
  groupOptions: SimpleSelectOption<GroupId>[];
  playbookOptions: readonly RoutingPlaybookOption[];
  selectedGroupId: GroupId | undefined;
  selectedPlaybookIds: readonly PlaybookId[];
  /** Default candidate for multi-playbook rules; undefined for single rules. */
  selectedDefaultPlaybookId: PlaybookId | undefined;
  onSave: (rule: PlaybookRoutingGroupRule) => void;
  onDelete?: () => void;
  /** If true, the card starts in edit mode (used for new rules) */
  isNew?: boolean;
  onCancelNew?: () => void;
}

/** Joins playbook names as "A", "A or B", "A, B, or C". */
const playbookNameListFormatter = new Intl.ListFormat("en-US", {
  style: "short",
  type: "disjunction",
});

function oldestPlaybookId(
  playbookIds: readonly PlaybookId[],
  playbookOptions: readonly RoutingPlaybookOption[],
): PlaybookId | undefined {
  const selected = playbookOptions.filter((o) => playbookIds.includes(o.value));
  if (selected.length === 0) {
    return undefined;
  }
  return selected.reduce((oldest, option) =>
    option.createdAt < oldest.createdAt ? option : oldest,
  ).value;
}

export const RoutingRuleCard: FC<RoutingRuleCardProps> = ({
  groupOptions,
  playbookOptions,
  selectedGroupId,
  selectedPlaybookIds,
  selectedDefaultPlaybookId,
  onSave,
  onDelete,
  isNew = false,
  onCancelNew,
}) => {
  const [isEditing, setIsEditing] = useState(isNew);
  const [editingGroupId, setEditingGroupId] = useState(selectedGroupId);
  const [editingPlaybookIds, setEditingPlaybookIds] =
    useState<readonly PlaybookId[]>(selectedPlaybookIds);
  const [editingDefaultPlaybookId, setEditingDefaultPlaybookId] = useState(
    selectedDefaultPlaybookId,
  );

  const { renderConfirmationModal, onConfirmThenClose } = useConfirmationModal({
    needsConfirmation: true,
    onClose: onDelete ?? (() => {}),
  });

  const playbookNameById = useMemo(
    () => new Map(playbookOptions.map((o) => [o.value, o.label])),
    [playbookOptions],
  );

  const playbookSections = useMemo(
    () => [
      {
        id: "playbooks",
        label: "Select one or more",
        options: playbookOptions.map<MultiSelectOption>((o) => ({
          id: o.value,
          label: o.label,
        })),
      },
    ],
    [playbookOptions],
  );

  const selectedGroupName = groupOptions.find(
    (o) => o.value === selectedGroupId,
  )?.label;

  const handleEdit = () => {
    setEditingGroupId(selectedGroupId);
    setEditingPlaybookIds(selectedPlaybookIds);
    setEditingDefaultPlaybookId(selectedDefaultPlaybookId);
    setIsEditing(true);
  };

  const handleCancel = () => {
    if (isNew) {
      onCancelNew?.();
    } else {
      setEditingGroupId(selectedGroupId);
      setEditingPlaybookIds(selectedPlaybookIds);
      setEditingDefaultPlaybookId(selectedDefaultPlaybookId);
      setIsEditing(false);
    }
  };

  const handlePlaybookIdsChange = (ids: readonly string[]) => {
    const playbookIds = ids.map((id) => asPlaybookId(id));
    setEditingPlaybookIds(playbookIds);

    if (playbookIds.length < 2) {
      // Single-playbook rules have no default candidate.
      setEditingDefaultPlaybookId(undefined);
    } else if (
      !editingDefaultPlaybookId ||
      !playbookIds.includes(editingDefaultPlaybookId)
    ) {
      // Default to the oldest playbook; the user can override below.
      setEditingDefaultPlaybookId(
        oldestPlaybookId(playbookIds, playbookOptions),
      );
    }
  };

  const buildRule = (): PlaybookRoutingGroupRule | null => {
    if (!editingGroupId || editingPlaybookIds.length === 0) {
      return null;
    }
    if (editingPlaybookIds.length === 1) {
      const playbookId = editingPlaybookIds[0];
      if (playbookId === undefined) {
        return null;
      }
      return {
        kind: "single",
        group_id: editingGroupId,
        playbook_id: playbookId,
      };
    }
    if (!editingDefaultPlaybookId) {
      return null;
    }
    return {
      kind: "multi",
      group_id: editingGroupId,
      playbook_ids: [...editingPlaybookIds],
      default_playbook_id: editingDefaultPlaybookId,
    };
  };

  const handleSave = () => {
    const rule = buildRule();
    if (rule) {
      onSave(rule);
      setIsEditing(false);
    }
  };

  if (isEditing) {
    const firstEditingPlaybookId = editingPlaybookIds[0];
    const playbookPickerLabel =
      editingPlaybookIds.length === 0 || firstEditingPlaybookId === undefined
        ? undefined
        : editingPlaybookIds.length === 1
          ? (playbookNameById.get(firstEditingPlaybookId) ?? "1 playbook")
          : `one of ${editingPlaybookIds.length} playbooks`;

    return (
      <div className="relative rounded-lg border border-border-light bg-white p-4">
        <div className="flex w-full flex-nowrap items-center gap-1 overflow-hidden text-sm text-foreground">
          <span className="shrink-0">For teammates in</span>
          <div className="flex w-fit max-w-52 min-w-0 shrink-0 [&_button]:max-w-52">
            <SimpleSelect
              options={groupOptions}
              value={editingGroupId ?? null}
              onChange={setEditingGroupId}
              placeholder="Select group"
            />
          </div>
          <span className="shrink-0">, route to</span>
          <div className="flex min-w-0 shrink items-center gap-1">
            <div className="flex w-fit max-w-52 min-w-0 shrink [&_button]:max-w-52">
              <MultiSelect
                sections={playbookSections}
                value={editingPlaybookIds}
                onChange={handlePlaybookIdsChange}
                label={playbookPickerLabel}
                placeholder="Select playbook(s)"
              />
            </div>
            <span className="shrink-0">.</span>
          </div>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant={ButtonVariant.Secondary} onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            variant={ButtonVariant.Primary}
            onClick={handleSave}
            isDisabled={!buildRule()}
          >
            Save
          </Button>
        </div>
      </div>
    );
  }

  const selectedPlaybookNames = selectedPlaybookIds.map(
    (id) => playbookNameById.get(id) ?? "Unknown playbook",
  );
  const isMulti = selectedPlaybookIds.length >= 2;

  return (
    <div className="group relative rounded-lg border border-border-light bg-white p-4">
      <p className="text-sm text-foreground">
        For teammates in <span className="font-bold">{selectedGroupName}</span>,{" "}
        {isMulti ? (
          <>
            route each call to the best match from{" "}
            {playbookNameListFormatter
              .formatToParts(selectedPlaybookNames)
              .map((part, index) =>
                part.type === "element" ? (
                  <span key={index} className="font-bold">
                    {part.value}
                  </span>
                ) : (
                  part.value
                ),
              )}
            .
          </>
        ) : (
          <>
            route to{" "}
            <span className="font-bold">{selectedPlaybookNames[0]}</span>.
          </>
        )}
      </p>
      <div className="absolute top-4 right-4 flex items-center gap-2 bg-linear-to-l from-white via-white to-transparent pl-8 opacity-0 transition-opacity group-hover:opacity-100">
        <IconButton
          variant={ButtonVariant.Secondary}
          icon={<IconPencil className="size-4" />}
          onClick={handleEdit}
        />
        {onDelete && (
          <IconButton
            variant={ButtonVariant.Secondary}
            icon={<IconTrash className="size-4" />}
            onClick={onConfirmThenClose}
          />
        )}
      </div>
      {renderConfirmationModal({
        title: "Delete routing rule",
        message: "Are you sure you want to delete this routing rule?",
        confirmText: "Delete",
      })}
    </div>
  );
};
